import React, { useState } from 'react';
import { Table, Button, Input, Space, Tag, message, Dropdown, Select } from 'antd';
import { PlusOutlined, SearchOutlined, MoreOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import CompanyManagementModal from '../components/common/CompanyManagementModal';
import { AddressTableCell } from '../components/common/AddressDisplay';
import { Card } from '../components/design-system/Card';

type CommunicationType = 'CONTRACTS' | 'BOOKING_REQUESTS' | 'SHIPPING_INSTRUCTIONS' | 'SHIPPING_DOCS';
type RecipientType = 'TO' | 'CC';

interface CommunicationPreference {
  communicationType: CommunicationType;
  recipientType: RecipientType;
  enabled: boolean;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title?: string;
  department?: string;
  isPrimary: boolean;
  communicationPreferences?: CommunicationPreference[];
}

interface Company {
  id: string;
  name: string;
  shortCode: string;
  shortName?: string;
  type: 'SUPPLIER' | 'BUYER' | 'BOTH';
  address?: string;
  country?: string;
  contractCount?: number;
  status: 'ACTIVE' | 'INACTIVE';
  contacts: Contact[];
  createdAt: string;
  updatedAt: string;
}

const CompaniesList: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'supplier' | 'buyer'>('all');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch companies
  const { data: companies, isLoading, error } = useQuery<Company[]>({
    queryKey: ['companies', searchTerm, typeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (typeFilter !== 'all') params.append('type', typeFilter);

      const response = await fetch(`/api/v1/companies?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch companies');
      const data = await response.json();
      return data.companies || [];
    },
    refetchInterval: 60000, // Refresh every minute
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (companyId: string) => {
      const response = await fetch(`/api/v1/companies/${companyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to delete company');
    },
    onSuccess: () => {
      message.success('Company deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
    onError: (error) => {
      message.error('Failed to delete company');
      console.error('Delete error:', error);
    },
  });

  const handleCreateCompany = () => {
    setSelectedCompany(null);
    setModalMode('create');
    setModalVisible(true);
  };

  const handleEditCompany = (company: Company) => {
    setSelectedCompany(company);
    setModalMode('edit');
    setModalVisible(true);
  };

  const handleDeleteCompany = (companyId: string) => {
    deleteMutation.mutate(companyId);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedCompany(null);
    // Refresh the companies list
    queryClient.invalidateQueries({ queryKey: ['companies'] });
  };

  const getCompanyTypeColor = (type: string) => {
    switch (type) {
      case 'SUPPLIER': return 'blue';
      case 'BUYER': return 'green';
      case 'BOTH': return 'purple';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'ACTIVE' ? 'success' : 'default';
  };

  const columns = [
    {
      title: 'Company Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Company, b: Company) => a.name.localeCompare(b.name),
      render: (text: string, record: Company) => (
        <div>
          <div className="text-card-subtitle">{text}</div>
          <div className="text-card-body" style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
            {record.shortName && <span style={{ marginRight: '8px' }}>{record.shortName}</span>}
            <span style={{ color: 'var(--text-tertiary)' }}>({record.shortCode})</span>
          </div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      filters: [
        { text: 'Supplier', value: 'SUPPLIER' },
        { text: 'Buyer', value: 'BUYER' },
        { text: 'Both', value: 'BOTH' },
      ],
      onFilter: (value: any, record: Company) => record.type === value,
      render: (type: string) => (
        <Tag color={getCompanyTypeColor(type)}>
          {type === 'BOTH' ? 'Supplier & Buyer' : type}
        </Tag>
      ),
    },
    {
      title: 'Contacts',
      key: 'contacts',
      render: (record: Company) => {
        if (!record.contacts || record.contacts.length === 0) {
          return <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>No contacts</span>;
        }

        // Show primary contact first, then others
        const primaryContact = record.contacts.find(c => c.isPrimary);
        const otherContacts = record.contacts.filter(c => !c.isPrimary);
        const displayContacts = primaryContact ? [primaryContact, ...otherContacts] : record.contacts;

        return (
          <div>
            {displayContacts.slice(0, 2).map((contact, index) => (
              <div key={contact.id} style={{ marginBottom: index < displayContacts.length - 1 ? '8px' : 0 }}>
                <div className="text-card-body">
                  {contact.firstName} {contact.lastName}
                  {contact.isPrimary && (
                    <Tag color="blue" style={{ marginLeft: '8px', fontSize: '10px' }}>PRIMARY</Tag>
                  )}
                </div>
                {contact.title && (
                  <div className="text-card-body" style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                    {contact.title}
                  </div>
                )}
                <div className="text-card-body" style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                  {contact.email}
                </div>
                {contact.phone && (
                  <div className="text-card-body" style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                    {contact.phone}
                  </div>
                )}
                {contact.communicationPreferences && contact.communicationPreferences.length > 0 && (
                  <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {contact.communicationPreferences.filter(p => p.enabled).map(pref => {
                      const labels: Record<CommunicationType, string> = {
                        'CONTRACTS': 'Contracts',
                        'BOOKING_REQUESTS': 'Bookings',
                        'SHIPPING_INSTRUCTIONS': 'SI',
                        'SHIPPING_DOCS': 'Docs',
                      };
                      return (
                        <Tag key={`${pref.communicationType}-${pref.recipientType}`} color="green" style={{ fontSize: '9px', margin: 0, padding: '0 4px' }}>
                          {labels[pref.communicationType]} ({pref.recipientType})
                        </Tag>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
            {record.contacts.length > 2 && (
              <div className="text-card-body" style={{ color: 'var(--text-tertiary)', fontSize: '11px', marginTop: '4px' }}>
                +{record.contacts.length - 2} more
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Location',
      key: 'location',
      render: (record: Company) => (
        <AddressTableCell 
          addressData={{ address: record.address, country: record.country }}
          showCountry={true}
        />
      ),
    },
    {
      title: 'Contracts',
      dataIndex: 'contractCount',
      key: 'contractCount',
      sorter: (a: Company, b: Company) => (a.contractCount || 0) - (b.contractCount || 0),
      render: (count: number) => count || 0,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Active', value: 'ACTIVE' },
        { text: 'Inactive', value: 'INACTIVE' },
      ],
      onFilter: (value: any, record: Company) => record.status === value,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (record: Company) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Edit',
                onClick: () => handleEditCompany(record),
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Delete',
                danger: true,
                onClick: () => handleDeleteCompany(record.id),
              },
            ],
          }}
          trigger={['click']}
        >
          <Button 
            type="text" 
            icon={<MoreOutlined />} 
            size="small"
            style={{ color: 'var(--text-secondary)' }}
          />
        </Dropdown>
      ),
    },
  ];

  if (error) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <h2 className="text-h2" style={{ color: 'var(--color-negative)' }}>Unable to Load Companies</h2>
        <p className="text-card-body" style={{ color: 'var(--text-secondary)', marginTop: '16px' }}>
          Please check your connection and try again
        </p>
        <Button 
          type="primary" 
          onClick={() => window.location.reload()}
          style={{ marginTop: '24px' }}
        >
          Refresh Page
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px' 
      }}>
        <div>
          <h1 className="text-h1">Companies</h1>
          <p className="text-card-body" style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>
            Manage suppliers, buyers, and trading partners
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreateCompany}
          size="large"
        >
          Add Company
        </Button>
      </div>

      {/* Search and Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Space size="middle" style={{ width: '100%', flexWrap: 'wrap' }}>
          <Input
            placeholder="Search companies..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            value={typeFilter}
            onChange={setTypeFilter}
            style={{ width: 180 }}
            options={[
              { value: 'all', label: 'All Companies' },
              { value: 'supplier', label: 'Suppliers Only' },
              { value: 'buyer', label: 'Buyers Only' },
            ]}
          />
          <div className="text-card-body" style={{ color: 'var(--text-secondary)' }}>
            {companies?.length || 0} companies found
          </div>
        </Space>
      </Card>

      {/* Companies Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={companies}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: companies?.length || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} companies`,
            pageSizeOptions: [10, 20, 50, 100],
            locale: {
              items_per_page: '/ page',
            },
            onChange: (page, newPageSize) => {
              setCurrentPage(page);
              if (newPageSize !== pageSize) {
                setPageSize(newPageSize);
                setCurrentPage(1); // Reset to first page when page size changes
              }
            },
            onShowSizeChange: (current, size) => {
              setPageSize(size);
              setCurrentPage(1);
            },
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* Company Modal */}
      <CompanyManagementModal
        visible={modalVisible}
        mode={modalMode}
        company={selectedCompany}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default CompaniesList;